import { createIdempotencyKey } from "@lafayette/api-client";
import type {
  StaffImportCancelBody,
  StaffImportCreateDraftBody,
  StaffImportJobDetail,
  StaffImportSaveMappingBody,
  StaffImportSubmitBody,
} from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import type { ImportCommandName } from "./data-provider";
import { describeImportError, type ImportFailure } from "./errors";
import { importKeys, useImportJobFetcher, useImportRequest } from "./queries";

// Runs a job command (W2 to W5) through the data provider's `custom` call and keeps the cached job
// current. Three rules live here:
//   1. Idempotency. One user action has one key. After a failure where the same command may run again
//      (no answer, a 503) the next identical attempt reuses the key, so the server runs it once.
//   2. Conflict. On a 409 the job is read again and written to the cache. The caller keeps the local
//      decisions and tells the staff member "Changed by someone else".
//   3. The cache. A success writes the detail the server returned into the job's cache entry and marks
//      the lists and the row pages stale.

type JobInputs = {
  createDraft: StaffImportCreateDraftBody;
  saveMapping: StaffImportSaveMappingBody;
  submitMapping: StaffImportSubmitBody;
  cancelJob: StaffImportCancelBody;
};
type JobCommandName = keyof JobInputs;

export type CommandOutcome =
  | Readonly<{ ok: true; detail: StaffImportJobDetail }>
  | Readonly<{
      ok: false;
      failure: ImportFailure;
      /** The server refused because of the job's state (a 409). */
      conflict: boolean;
      /** The job as the server holds it after a conflict; `null` when it could not be read again. */
      latest: StaffImportJobDetail | null;
    }>;

export function useImportCommands() {
  const request = useImportRequest();
  const fetchJob = useImportJobFetcher();
  const queryClient = useQueryClient();
  const pendingKey = useRef<{ signature: string; key: string } | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  const run = useCallback(
    async <K extends JobCommandName>(
      command: K,
      jobId: string | undefined,
      input: JobInputs[K],
    ): Promise<CommandOutcome> => {
      const signature = JSON.stringify([command, jobId ?? null, input]);
      const key =
        pendingKey.current?.signature === signature
          ? pendingKey.current.key
          : createIdempotencyKey();
      pendingKey.current = { signature, key };

      setPendingCount((count) => count + 1);
      try {
        const detail = await request<StaffImportJobDetail>(
          {
            command: command as ImportCommandName,
            ...(jobId === undefined ? {} : { jobId }),
            idempotencyKey: key,
          },
          input,
        );
        pendingKey.current = null;
        queryClient.setQueryData(importKeys.job(detail.job.id), detail);
        void queryClient.invalidateQueries({ queryKey: importKeys.lists() });
        void queryClient.invalidateQueries({ queryKey: importKeys.rows(detail.job.id) });
        return { ok: true, detail };
      } catch (error) {
        const failure = describeImportError(error);
        if (!failure.retryable) pendingKey.current = null;
        if (failure.kind !== "conflict" || jobId === undefined) {
          return { ok: false, failure, conflict: false, latest: null };
        }
        try {
          const latest = await fetchJob(jobId);
          queryClient.setQueryData(importKeys.job(jobId), latest);
          return { ok: false, failure, conflict: true, latest };
        } catch {
          return { ok: false, failure, conflict: true, latest: null };
        }
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [fetchJob, queryClient, request],
  );

  return { run, pending: pendingCount > 0 };
}

export type SaveMessage = Readonly<{
  tone: "success" | "error" | "conflict";
  text: string;
}>;

export const CHANGED_BY_SOMEONE_ELSE = "Changed by someone else";

/** The sentence under the buttons after a command. A conflict says so in plain words. */
export function describeOutcome(outcome: CommandOutcome, successText: string): SaveMessage {
  if (outcome.ok) return { tone: "success", text: successText };
  if (outcome.conflict) {
    return {
      tone: "conflict",
      text: `${CHANGED_BY_SOMEONE_ELSE}. This import was saved, submitted or cancelled by another person. Your choices on this page are still here. Check them and save again.`,
    };
  }
  return { tone: "error", text: outcome.failure.message };
}
